import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Container from '@/components/base/Container';
import { articles } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <SiteLayout title="Article not found | AVENOR">
        <Container className="pb-32 pt-40">
          <p className="font-label text-[11px] uppercase tracking-[0.14em] text-primary-500">Not in the archive</p>
          <h1 className="mt-6 text-4xl font-light md:text-6xl">This article could not be found.</h1>
          <Link to="/insights" className="ed-link mt-10 inline-block cursor-pointer font-label text-[11px] uppercase tracking-[0.14em]">Back to insights</Link>
        </Container>
      </SiteLayout>
    );
  }

  const others = articles.filter((a) => a.slug !== article.slug).slice(0, 2);
  const mid = Math.ceil(article.body.length / 2);

  return (
    <SiteLayout title={`${article.title} | AVENOR Insights`}>
      <article>
        <header className="paper-texture pb-12 pt-32 md:pb-16 md:pt-44">
          <Container>
            <Link to="/insights" className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700 hover:text-primary-500">
              <i className="ri-arrow-left-line" /> All insights
            </Link>
            <div className="mt-10 grid grid-cols-1 gap-8 border-t border-background-200 pt-8 lg:grid-cols-12 lg:gap-6">
              <div className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700 lg:col-span-2">
                <p className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary-500" />{article.category}</p>
                <p className="mt-3">{article.date}</p>
                <p className="mt-1">{article.readTime}</p>
              </div>
              <div className="lg:col-span-9">
                <h1 className="font-heading text-4xl font-light leading-[1.08] tracking-[-0.02em] md:text-6xl">{article.title}</h1>
                <p className="mt-6 text-sm text-foreground-700">By {article.author}</p>
              </div>
            </div>
          </Container>
        </header>

        <Container>
          <div className="aspect-[16/9] w-full overflow-hidden bg-background-200">
            <img src={article.image} alt={article.title} title={article.title} className="editorial-img h-full w-full object-cover object-top" />
          </div>
        </Container>

        <Container className="py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-7 lg:col-start-3">
              <p className="text-xl font-light leading-[1.6] text-foreground-950 md:text-2xl">{article.excerpt}</p>
              <div className="mt-10 space-y-6">
                {article.body.slice(0, mid).map((p) => (
                  <p key={p.slice(0, 30)} className="text-base leading-[1.75] text-foreground-800 md:text-[17px]">{p}</p>
                ))}
              </div>
              <blockquote className="my-12 border-y border-foreground-950 py-8">
                <p className="font-poster text-3xl font-semibold uppercase leading-[1.1] tracking-[0.02em] text-primary-500 md:text-4xl">&ldquo;{article.pullQuote}&rdquo;</p>
              </blockquote>
              <div className="space-y-6">
                {article.body.slice(mid).map((p) => (
                  <p key={p.slice(0, 30)} className="text-base leading-[1.75] text-foreground-800 md:text-[17px]">{p}</p>
                ))}
              </div>
              <p className="mt-12 border-t border-background-200 pt-6 text-xs leading-relaxed text-foreground-600">
                This article is general commentary and does not constitute professional advice. Please seek advice on your specific circumstances.
              </p>
            </div>
          </div>
        </Container>
      </article>

      <section className="border-t border-background-200 bg-background-100 py-16 md:py-24">
        <Container>
          <p className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-700">Continue reading</p>
          <div className="mt-8 grid grid-cols-1 gap-10 md:grid-cols-2">
            {others.map((a, i) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className={`group block cursor-pointer border-background-300/80 ${i ? 'md:border-l md:pl-8' : 'md:pr-8'}`}>
                <p className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-600">{a.category} — {a.date}</p>
                <h3 className="mt-4 text-xl font-normal leading-snug md:text-2xl"><span className="ed-link">{a.title}</span></h3>
              </Link>
            ))}
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}